import React, { useState, Fragment } from "react"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { MdClose } from "react-icons/md"
import { useTranslation } from "react-i18next"

import {
  Button,
  IconButton,
  Modal,
  CartStockValidationResultModal
} from "components"
import {
  clearCart,
  printOrder,
  convertOrderItemsToCartItems,
  convertOrderCombosToCartCombos,
  validateCartContents
} from "utils"
import { editOrder, sendOrder } from "services"
import classes from "./index.module.scss"
import clsx from "clsx"

const initialSendingState = {
  newOrder: false,
  editOrder: false
}

const FoodSidebarEditOrderModal = props => {
  const {
    tableNumber,
    onExited,
    singleTableOrderDetails,
    openEditOrderModal,
    setOpenEditOrderModal,
    onNewOrderCreated = () => { },
    onOrderCombined = () => { }
  } = props

  const [t, i18n] = useTranslation()

  const deliveryInfo =
    useSelector(state => state.deliveryInfo)

  const order =
    useSelector(state => state.order)

  const branchId =
    useSelector(state => state.userAccountInfo.branchId)

  const [sending, setSending] = useState(initialSendingState)
  const [problematicItems, setProblematicItems] = useState([])
  const [openCartValidationResultModal, setOpenCartValidationResultModal] = useState(false)

  const validateCart = async (previousOrderItems) => {
    const problematicItems =
      await validateCartContents(
        order.items,
        order.combos,
        previousOrderItems
      )

    if (problematicItems.length === 0) return true

    setProblematicItems(
      problematicItems
    )

    setOpenCartValidationResultModal(true)

    setSending({
      newOrder: false,
      editOrder: false
    })

    return false
  }

  const getRequestBody = (previousOrderItems) => {
    const newOrderItems =
      [...order.items, ...previousOrderItems.items]

    const orderItems =
      newOrderItems.map(item => ({
        asComboSet: false,
        optionId: item.optionId,
        foodId: item.foodId,
        quantity: item.quantity,
        systemDiscount: item.systemDiscount * item.quantity,
        specificationItemIds:
          item.specificationItems.map(si =>
            si.specificationItemId || si._id
          )
      }))

    const newOrderCombos =
      [...order.combos, ...previousOrderItems.combos]

    const orderComboItems = []

    newOrderCombos.forEach((combo, cIndex) =>
      combo.items.forEach(comboItem =>
        orderComboItems.push({
          foodId: comboItem.foodId,
          optionId: comboItem.optionId,
          quantity: comboItem.quantity,
          specificationItemIds: [],
          comboSet: {
            code: combo.code,
            comboSetItemId: comboItem.comboSetItemId,
            groupNumber: cIndex + 1
          }
        })
      )
    )

    const couponCodes = []

    order.combos.forEach(combo =>
      couponCodes.push(combo.code)
    )

    previousOrderItems.combos.forEach(combo =>
      couponCodes.push(combo.code)
    )

    const items =
      [...orderItems, ...orderComboItems]

    const data = {
      items,
      isCod: true,
      branchId,
      couponCodes,
      areaName: deliveryInfo.areaName,
      deliveryType: "dineIn",
      tableNumber,
      isPrinted: true,
      paymentType: "cod"
    }

    return data
  }

  const handleCreateNewOrder = async () => {
    setSending(prev => ({ ...prev, newOrder: true }))

    try {
      const result = await validateCart()

      if (!result) return

      const previousOrderItems = {
        items: [],
        combos: []
      }

      const requestBody =
        getRequestBody(previousOrderItems)

      const { order } =
        await sendOrder(requestBody)

      onNewOrderCreated()

      setOpenEditOrderModal(false)

      clearCart()

      printOrder(order)
    } catch (err) {
      console.log(err)

      toast.error(t("عملیات_انجام_نشد"))

      setSending(prev => ({
        ...prev, newOrder: false
      }))
    }
  }

  const getPreviousOrderItems = () => {
    const tableOrder = singleTableOrderDetails.current

    const previousOrderItems =
      convertOrderItemsToCartItems(
        tableOrder.items
      )

    const previousOrderCombos =
      convertOrderCombosToCartCombos(
        tableOrder.items
      )

    return {
      items: previousOrderItems,
      combos: previousOrderCombos
    }
  }

  const handleEdit = async () => {
    setSending(prev => ({
      ...prev, editOrder: true
    }))

    try {
      const previousOrderItems =
        getPreviousOrderItems()

      const result =
        await validateCart(previousOrderItems)

      if (!result) return

      const requestBody =
        getRequestBody(previousOrderItems)

      const accountId =
        singleTableOrderDetails.current.accountId || null

      const query =
        singleTableOrderDetails.current.isGuestOrder
          ? { orderId: singleTableOrderDetails.current._id }
          : { accountId }

      await editOrder(
        singleTableOrderDetails.current._id,
        requestBody,
        query
      )

      setOpenEditOrderModal(false)

      clearCart()

      onOrderCombined()
    } catch (err) {
      console.log(err)


      toast.error(t("عملیات_انجام_نشد"))

      setSending(prev => ({
        ...prev, editOrder: false
      }))
    }
  }

  const isLtr = i18n.dir(i18n.language) === "ltr"

  return (
    <Fragment>
      <Modal
        onExited={onExited}
        show={openEditOrderModal}
        onHide={() => setOpenEditOrderModal(false)}
      >
        <div className={classes["container"]}>
          <div className={classes["header"]}>
            <IconButton
              icon={MdClose}
              size={20}
              onClick={() => setOpenEditOrderModal(false)}
            />
          </div>

          <div className={classes["title"]}>
            {t("آیا_می_خواهید_سفارش_جدیدی_را_ثبت_کنید_یا_با_سفارش_قبلی_ترکیب_کنید")}
          </div>

          <div className={classes["footer"]}>
            <Button
              color="success"
              loading={sending.newOrder}
              disabled={sending.editOrder}
              onClick={handleCreateNewOrder}
              className={clsx(classes["button"], isLtr && classes["button-ltr"])}
            >
              {t("سفارش_جدید")}
            </Button>

            <Button
              color="success"
              loading={sending.editOrder}
              disabled={sending.newOrder}
              onClick={handleEdit}
            >
              {t("ترکیب_سفارش")}
            </Button>
          </div>
        </div>
      </Modal>

      <CartStockValidationResultModal
        problematicItems={problematicItems}
        openCartValidationResultModal={openCartValidationResultModal}
        setOpenCartValidationResultModal={setOpenCartValidationResultModal}
      />
    </Fragment>
  )
}

export default FoodSidebarEditOrderModal